/**
 * Note: this logic is duplicated from dynatrace-sdk, as design-system can't include sdk binaries
 */

/* eslint-disable no-console */

import { Logger, GlobalDtRuntimeWithLogger } from './sdk-logger.types.js';

/** @internal */
//globalThis is not supported in Chrome 69 and Safari 12
export const getGlobalWithDtRuntime = <T extends typeof globalThis>(): T =>
  (typeof globalThis !== 'undefined' ? globalThis : window) as unknown as T;

/**
 * Creates a logger instance for structured message recording.
 *
 * @param name - Identifier for the logger instance.
 * @returns An instance of the Logger utility.
 * @internal
 */
export const createSdkLogger = (name: string): Readonly<Logger> => {
  const loggerModule =
    getGlobalWithDtRuntime<GlobalDtRuntimeWithLogger>().dtRuntime?.logger;

  if (typeof loggerModule?.createLoggerV2 === 'function') {
    return loggerModule?.createLoggerV2(name);
  } else if (typeof loggerModule?.createLogger === 'function') {
    return loggerModule?.createLogger(name);
  } else {
    const wrapConsole =
      (
        logFunc:
          | typeof console.log
          | typeof console.warn
          | typeof console.error,
      ) =>
      (...args: Parameters<Logger['info' | 'warn' | 'error']>) =>
        logFunc(`[${name}]: ${args[0]}`, args[1]);

    return Object.freeze({
      info: wrapConsole(console.log),
      warn: wrapConsole(console.warn),
      error: wrapConsole(console.error),
    } as Logger);
  }
};

/**
 * Checks if the logger v2 create fn is available on platform
 */
export const supportsLoggerV2 = () =>
  typeof getGlobalWithDtRuntime<GlobalDtRuntimeWithLogger>().dtRuntime?.logger
    ?.createLoggerV2 === 'function';
